import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import Image from '../../components/AppImage';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import useCartStore from '../../store/cartStore';
import { adminService } from '../../services/adminService';

const ShoppingCart = () => {
  const navigate = useNavigate();
  const { items, removeItem, updateQuantity, clearCart, getTotal, getItemCount, initSession } = useCartStore();
  const [whatsappPhone, setWhatsappPhone] = useState('+55 21 96717-4817');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    initSession();
    loadWhatsAppNumber();
  }, []);

  const loadWhatsAppNumber = async () => {
    try {
      const settings = await adminService?.getSettings();
      if (settings?.whatsapp_phone) {
        setWhatsappPhone(settings?.whatsapp_phone);
      }
    } catch (err) {
      console.error('Error loading WhatsApp number:', err);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleCheckout = () => {
    if (items?.length === 0) return;

    // Build WhatsApp message
    let message = '✨ *Solicitação de Compra - UP MIDIA* ✨\n\n';
    message += '🛒 *Itens do Carrinho:*\n\n';

    items?.forEach((item, index) => {
      message += `${index + 1}. *${item?.title || item?.name}*\n`;
      message += `   Tipo: ${item?.type === 'content' ? (item?.contentType === 'video' ? 'Vídeo' : 'Arte') : 'Plano'}\n`;
      if (item?.quantity > 1) {
        message += `   Quantidade: ${item?.quantity}\n`;
      }
      message += `   Preço: R$ ${(item?.price * item?.quantity)?.toFixed(2)}\n\n`;
    });

    message += `💰 *Total: R$ ${getTotal()?.toFixed(2)}*\n\n`;
    message += '👉 Gostaria de finalizar esta compra!';

    const encodedMessage = encodeURIComponent(message);
    const phoneNumber = whatsappPhone?.replace(/\D/g, '');
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;

    window.open(whatsappUrl, '_blank');
  };

  const handleQuantityChange = (itemId, itemType, newQuantity) => {
    const quantity = parseInt(newQuantity) || 1;
    if (quantity < 1) return;
    updateQuantity(itemId, itemType, quantity);
  };

  return (
    <>
      <Helmet>
        <title>Carrinho de Compras - UP MIDIA</title>
        <meta name="description" content="Revise seus itens e finalize sua compra via WhatsApp" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-50 bg-card/95 backdrop-blur-sm border-b border-border glow-primary">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-3">
                <img
                  src="/assets/images/up_menor-1769135382338.png"
                  alt="UP MIDIA platform logo"
                  className="h-10 md:h-12 w-auto"
                />
                <div>
                  <h1 className="text-xl md:text-2xl font-heading font-bold text-primary">
                    UP MIDIA
                  </h1>
                  <p className="text-xs text-muted-foreground hidden md:block">
                    Carrinho de Compras
                  </p>
                </div>
              </Link>
              <Link to="/">
                <Button variant="ghost" iconName="ArrowLeft">
                  Voltar
                </Button>
              </Link>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-4 py-8">
          {items?.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-6">
                <Icon name="ShoppingCart" size={40} color="var(--color-muted-foreground)" />
              </div>
              <h2 className="text-2xl font-heading font-bold text-foreground mb-3">
                Seu carrinho está vazio
              </h2>
              <p className="text-muted-foreground mb-6">
                Adicione artes, vídeos ou planos para começar
              </p>
              <Link to="/">
                <Button iconName="ArrowLeft" className="glow-primary">
                  Explorar Galeria
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-4">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-heading font-bold text-foreground">
                    Itens do Carrinho ({getItemCount()})
                  </h2>
                  <Button
                    onClick={clearCart}
                    variant="ghost"
                    iconName="Trash2"
                    size="sm"
                  >
                    Limpar Carrinho
                  </Button>
                </div>

                {items?.map((item) => (
                  <div
                    key={`${item?.id}-${item?.type}`}
                    className="bg-card rounded-lg p-4 md:p-6 glow-primary hover:glow-primary-hover transition-smooth"
                  >
                    <div className="flex gap-4">
                      <div className="relative w-24 h-24 md:w-32 md:h-32 flex-shrink-0">
                        <Image
                          src={item?.thumbnail || item?.coverImage}
                          alt={item?.thumbnailAlt || `Imagem de ${item?.title || item?.name}`}
                          className="w-full h-full object-cover rounded-md"
                        />
                        {item?.type === 'content' && item?.contentType === 'video' && (
                          <div className="absolute top-2 right-2 w-8 h-8 bg-secondary/90 rounded-full flex items-center justify-center">
                            <Icon name="Play" size={16} color="var(--color-background)" />
                          </div>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-heading font-bold text-foreground mb-1 line-clamp-2">
                              {item?.title || item?.name}
                            </h3>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <span className="px-2 py-0.5 bg-primary/20 text-primary rounded text-xs">
                                {item?.type === 'content'
                                  ? item?.contentType === 'video' ?'Vídeo' :'Arte' :'Plano'}
                              </span>
                              {item?.category && (
                                <span className="text-xs">{item?.category}</span>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => removeItem(item?.id, item?.type)}
                            className="w-8 h-8 rounded-full bg-muted flex items-center justify-center hover:bg-red-500/20 hover:text-red-500 transition-smooth"
                          >
                            <Icon name="X" size={16} />
                          </button>
                        </div>

                        <div className="flex items-center justify-between mt-4">
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-muted-foreground">Quantidade:</span>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleQuantityChange(item?.id, item?.type, item?.quantity - 1)}
                                className="w-8 h-8 rounded-md bg-muted flex items-center justify-center hover:bg-muted/80 transition-smooth"
                              >
                                <Icon name="Minus" size={16} />
                              </button>
                              <input
                                type="number"
                                min="1"
                                value={item?.quantity}
                                onChange={(e) => handleQuantityChange(item?.id, item?.type, e?.target?.value)}
                                className="w-16 px-2 py-1 text-center bg-background border border-border rounded-md text-foreground"
                              />
                              <button
                                onClick={() => handleQuantityChange(item?.id, item?.type, item?.quantity + 1)}
                                className="w-8 h-8 rounded-md bg-muted flex items-center justify-center hover:bg-muted/80 transition-smooth"
                              >
                                <Icon name="Plus" size={16} />
                              </button>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-xl font-bold text-accent">
                              R$ {(item?.price * item?.quantity)?.toFixed(2)}
                            </div>
                            {item?.quantity > 1 && (
                              <div className="text-xs text-muted-foreground">
                                R$ {item?.price?.toFixed(2)} cada
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="lg:col-span-1">
                <div className="bg-card rounded-lg p-6 glow-primary sticky top-24">
                  <h3 className="text-xl font-heading font-bold text-foreground mb-6">
                    Resumo do Pedido
                  </h3>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span className="font-medium text-foreground">
                        R$ {getTotal()?.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Itens</span>
                      <span className="font-medium text-foreground">{getItemCount()}</span>
                    </div>
                  </div>

                  <div className="border-t border-border pt-4 mb-6">
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-heading font-bold text-foreground">Total</span>
                      <span className="text-2xl font-bold text-accent">
                        R$ {getTotal()?.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <Button
                    onClick={handleCheckout}
                    iconName="MessageCircle"
                    fullWidth
                    className="glow-secondary mb-4"
                  >
                    Finalizar via WhatsApp
                  </Button>

                  <div className="bg-secondary/10 rounded-lg p-4 text-sm text-muted-foreground">
                    <div className="flex items-start gap-2">
                      <Icon name="Info" size={16} className="flex-shrink-0 mt-0.5" />
                      <p>
                        Ao clicar em finalizar, você será redirecionado para o WhatsApp com todos os
                        itens do carrinho listados.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ShoppingCart;